import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, Button, Alert, Form } from "react-bootstrap";
import { setToken } from "../utils/auth";
import { Helmet } from "react-helmet-async";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [redirecting, setRedirecting] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    try {
      const token = localStorage.getItem("token");
      const refreshToken = localStorage.getItem("refresh_token");
      if (token || refreshToken) {
        navigate("/", { replace: true });
        return;
      }
    } catch {}
    setRedirecting(false);
  }, [navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("activated") === "true") {
      setSuccess("✅ Your account has been successfully activated");
    }
    if (params.get("expired") === "true") {
      setError("expired");
    }
  }, [location.search]);

const metaDescription =
  "Login to IndexBooster to manage Google indexing projects, track Googlebot visits, control redirects, and monitor indexing status.";


  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({ username: email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 403) {
          if (data.detail === "Your account has been blocked. Contact support.") {
            setError("❌ Your account has been blocked. Contact support.");
          } else if (data.detail === "Account not activated.") {
            setError("Account not activated.");
          } else {
            setError("❌ Incorrect username or password");
          }
        } else {
          setError("❌ Incorrect username or password");
        }
        return;
      }

      setToken(data.access_token, data.refresh_token);
      navigate("/", { replace: true });
    } catch (err) {
      setError("❌ The server is temporarily unavailable. Please try again in 15 minutes.");
    }
  };

  if (redirecting) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>IndexBooster - Login to Your Google Indexing Dashboard</title>
        <meta name="description" content="Sign in to manage indexing projects, add URLs, check indexability, and monitor indexing results in one fast, secure dashboard." />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="IndexBooster — Login to Your Google Indexing Dashboard" />
        <meta property="og:description" content="Sign in to manage indexing projects, add URLs, check indexability, and monitor indexing results in one fast, secure dashboard." />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="IndexBooster — Login to Your Google Indexing Dashboard" />
        <meta name="twitter:description" content="Sign in to manage indexing projects, add URLs, check indexability, and monitor indexing results in one fast, secure dashboard." />
      </Helmet>

      <div className="d-flex justify-content-center align-items-center vh-100 bg-light">
        <Card style={{ width: "400px", padding: "20px" }}>
          <Card.Body>
            <h1 className="text-center">Login</h1>
            {success && <Alert variant="success">{success}</Alert>}

            {error === "Account not activated." ? (
              <Alert variant="danger">
                <div className="mb-2">
                  ❌ The account is not activated. Please follow the link in the email sent to you.
                </div>
                <div className="text-center">
                  <a href="/resend-activation">Resend the activation link</a>
                </div>
              </Alert>
            ) : error === "expired" ? (
              <Alert variant="danger">
                <div className="mb-2">
                  ❌ The activation link is invalid or has expired. You can request a{" "}
                  <a href="/resend-activation">new activation link.</a>
                </div>
              </Alert>
            ) : (
              error && <Alert variant="danger">{error}</Alert>
            )}

            <Form onSubmit={handleLogin}>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <div className="text-start mt-2">
                  <a href="/registration">Need an account?</a>
                </div>
                <div className="text-start mt-2">
                  <a href="/forgot-password">Forgot your password?</a>
                </div>
              </Form.Group>
              <Button variant="primary" type="submit" className="w-100 mt-3">
                Login
              </Button>
            </Form>
          </Card.Body>
        </Card>
      </div>
    </>
  );
};

export default Login;
